import { useMemo } from 'react';
import BillingDetails from './components/BillingDetails';
import BillingHistory from './components/BillingHistory';
import { Box, Typography, Paper } from '@mui/material';
import IntegrationUsage from './components/IntegrationUsage';
import ActiveAddOnsUsage from './components/ActiveAddOnsUsage';
import Grid from '@mui/material/Grid2';
import {
  useGetBusinessBillingHistory,
  useGetBusinessCancellationPreview,
  usePayDues,
  useCancelBusinessSubscription
} from '@/apis/business/setting/billing/hook';

import { IoIosInformationCircleOutline } from 'react-icons/io';
import { useGetBusinessProfile } from '@/apis/business/hook';
import Button from '@/components/Common/Button';
import { useGetCurrentUser } from '@/apis/user/hook';
import CancelSubscription, { buildCancelSubscriptionBillingPreview } from '@/components/Common/CancelSubscription';

export default function Billing() {
  const { data: BillingHistoryData, isLoading: loadingBilling } = useGetBusinessBillingHistory();
  const { data: cancellationPreviewData } = useGetBusinessCancellationPreview();
  const { data: businessProfile } = useGetBusinessProfile();
  const { mutate: payDues, isPending: payingDues } = usePayDues();
  const cancelBusinessSubscriptionMutation = useCancelBusinessSubscription();
  const cancelBillingPreview = useMemo(
    () => buildCancelSubscriptionBillingPreview(cancellationPreviewData),
    [cancellationPreviewData]
  );
  const { data: user, isLoading: loadingUser } = useGetCurrentUser();
  const resAccountStatus = businessProfile?.account_status;
  // we need to restrict business status behavior for agency user
  const accountStatus =
    !loadingUser && !['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(user?.role ?? '')
      ? resAccountStatus
      : 'active';
  const isPastDue = accountStatus === 'past_due';

  return (
    <Box>
      <Typography variant='h6' fontWeight={500}>
        Billing & Usage
      </Typography>
      <Typography color='secondary' fontSize={14}>
        Manage your subscription and track usage
      </Typography>
      {isPastDue && (
        <Box
          bgcolor='#FFFBEB'
          mt={2}
          padding={2}
          border='1px solid #F59E0B'
          borderRadius='10px'
          display='flex'
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Box display='flex' flexDirection='row' alignItems='center' gap={1}>
            <IoIosInformationCircleOutline color='#D97706' size={20} />
            <Box display='flex' flexDirection='column' gap={-2}>
              <Typography color='#D97706' fontSize={16} fontWeight={600} mb={-0.4}>
                Payment Pending
              </Typography>
              <Typography color='#D97706' fontSize={14}>
                Your previous invoice is overdue. Please complete payment to continue using all features.
              </Typography>
            </Box>
          </Box>
          <Button
            isPending={payingDues}
            sx={{ bgcolor: '#D97706', px: 5, minWidth: 40, fontWeight: 500, fontSize: '14px' }}
            onClick={() => payDues()}
          >
            Pay Now
          </Button>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid size={12}>
          <BillingDetails />
        </Grid>
        {BillingHistoryData?.integration_usage && (
          <Grid size={12}>
            <Paper sx={{ padding: 2, border: '1px solid #ececec', borderRadius: 2 }} elevation={0}>
              <IntegrationUsage data={BillingHistoryData?.integration_usage} isLoading={loadingBilling} />
            </Paper>
          </Grid>
        )}
        <Grid size={12}>
          <ActiveAddOnsUsage
            addonCharges={BillingHistoryData?.addon_charges}
            totalAddonAmount={BillingHistoryData?.total_addon_amount}
            isLoading={loadingBilling}
          />
        </Grid>
        <Grid size={12}>
          <Paper sx={{ p: 2, border: '1px solid #ececec', borderRadius: 2 }} elevation={0}>
            <BillingHistory />
          </Paper>
        </Grid>

        {businessProfile?.agency_billing_enabled && (
          <Grid size={12}>
            <CancelSubscription
              billingPreview={cancelBillingPreview}
              cancelSubscription={cancelBusinessSubscriptionMutation}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
